import { jsPDF } from 'jspdf';

// Constants for print quality
const TARGET_DPI = 300;
const PIXELS_PER_MM = TARGET_DPI / 25.4;

// Helper function to load an image
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

// Render badge image to a data URL
// Image fills to badgeSize (58mm), design area is larger (70mm)
const renderBadgeImage = async (badgeData, badgeConfig, designWidthMM, designHeightMM, badgeWidthMM, badgeHeightMM) => {
  // Create canvas at design area size (70mm for 58mm badge)
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(designWidthMM * PIXELS_PER_MM);
  canvas.height = Math.round(designHeightMM * PIXELS_PER_MM);
  const ctx = canvas.getContext('2d');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Fill white background for full design area
  ctx.fillStyle = '#ffffff';
  if (badgeConfig.type === 'round') {
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Draw image layer - IMAGE FILLS TO BADGE SIZE (58mm), NOT DESIGN AREA
  const imageLayer = badgeData?.layers?.find(layer => layer.type === 'image');

  if (imageLayer && imageLayer.url) {
    try {
      const img = await loadImage(imageLayer.url);

      const imgScale = imageLayer.scale || 1;
      const offsetX = imageLayer.offsetX || 0;
      const offsetY = imageLayer.offsetY || 0;
      const fitMode = imageLayer.fitMode || 'cover';

      const imgRatio = img.naturalWidth / img.naturalHeight;

      let drawWidthMM, drawHeightMM;

      if (fitMode === 'cover') {
        if (badgeConfig.type === 'round') {
          // For round badges, image must COVER the BADGE circle (Cut Size)
          // Scale based on the smaller dimension of the image to fill the diameter
          const diameter = badgeWidthMM;
          if (imgRatio >= 1) {
            // Image is wider than tall - height fills diameter
            drawHeightMM = diameter * imgScale;
            drawWidthMM = drawHeightMM * imgRatio;
          } else {
            // Image is taller than wide - width fills diameter
            drawWidthMM = diameter * imgScale;
            drawHeightMM = drawWidthMM / imgRatio;
          }
        } else {
          // For rectangle/square badges
          // Use BADGE dimensions for cover ratio to ensure visibility in cut area
          // This prevents ink waste in the bleed area as requested
          const badgeRatio = badgeWidthMM / badgeHeightMM;
          if (imgRatio > badgeRatio) {
            drawHeightMM = badgeHeightMM * imgScale;
            drawWidthMM = drawHeightMM * imgRatio;
          } else {
            drawWidthMM = badgeWidthMM * imgScale;
            drawHeightMM = drawWidthMM / imgRatio;
          }
        }
      } else {
        // Fit mode - contain within BADGE (cut size)
        const badgeRatio = badgeWidthMM / badgeHeightMM;
        if (imgRatio > badgeRatio) {
          drawWidthMM = badgeWidthMM * imgScale;
          drawHeightMM = drawWidthMM / imgRatio;
        } else {
          drawHeightMM = badgeHeightMM * imgScale;
          drawWidthMM = drawHeightMM * imgRatio;
        }
      }

      // Add explicit margin to image size to hide anti-aliasing edge artifacts
      const edgeMarginMM = 0.3; // 0.3mm margin on each side
      drawWidthMM += edgeMarginMM * 2;
      drawHeightMM += edgeMarginMM * 2;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      const drawWidthPx = drawWidthMM * PIXELS_PER_MM;
      const drawHeightPx = drawHeightMM * PIXELS_PER_MM;
      const drawX = centerX - drawWidthPx / 2 + (offsetX / 100) * (drawWidthPx - badgeWidthMM * PIXELS_PER_MM);
      const drawY = centerY - drawHeightPx / 2 + (offsetY / 100) * (drawHeightPx - badgeHeightMM * PIXELS_PER_MM);

      // Clip to BADGE size (Cut Size) to save ink
      ctx.save();
      ctx.beginPath();
      if (badgeConfig.type === 'round') {
        const badgeRadiusPx = (badgeWidthMM * PIXELS_PER_MM) / 2;
        ctx.arc(centerX, centerY, badgeRadiusPx, 0, Math.PI * 2);
        console.log(`Image clipped to badge radius ${badgeRadiusPx.toFixed(0)}px = ${badgeWidthMM / 2}mm (diameter ${badgeWidthMM}mm)`);
      } else {
        const bleedXPx = ((designWidthMM - badgeWidthMM) / 2) * PIXELS_PER_MM;
        const bleedYPx = ((designHeightMM - badgeHeightMM) / 2) * PIXELS_PER_MM;
        ctx.rect(bleedXPx, bleedYPx, badgeWidthMM * PIXELS_PER_MM, badgeHeightMM * PIXELS_PER_MM);
      }
      ctx.clip();

      ctx.drawImage(img, drawX, drawY, drawWidthPx, drawHeightPx);
      ctx.restore();

    } catch (error) {
      console.error('Error loading image:', error);
    }
  }

  return canvas.toDataURL('image/png');
};

export async function generatePDF({ badge, badges, orientation, showGuides }) {
  try {
    // A4 in mm
    const A4_WIDTH_MM = 210;
    const A4_HEIGHT_MM = 297;
    const MARGIN_MM = 15;
    const GUTTER_MM = 5;

    // Create PDF with mm units
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: false
    });

    // Get badge dimensions in MM
    let designWidthMM, designHeightMM, badgeWidthMM, badgeHeightMM;

    if (badge.type === 'round') {
      designWidthMM = designHeightMM = badge.designArea;  // e.g. 70mm
      badgeWidthMM = badgeHeightMM = badge.badgeSize;     // e.g. 58mm
    } else if (badge.type === 'square') {
      designWidthMM = designHeightMM = badge.designArea;
      badgeWidthMM = badgeHeightMM = badge.badgeSize;
    } else {
      if (orientation === 'landscape') {
        designWidthMM = badge.designWidth;
        designHeightMM = badge.designHeight;
        badgeWidthMM = badge.badgeWidth;
        badgeHeightMM = badge.badgeHeight;
      } else {
        designWidthMM = badge.designHeight;
        designHeightMM = badge.designWidth;
        badgeWidthMM = badge.badgeHeight;
        badgeHeightMM = badge.badgeWidth;
      }
    }

    console.log('=== PDF GENERATION ===');
    console.log(`Design Area: ${designWidthMM}mm x ${designHeightMM}mm`);
    console.log(`Badge Size (Cut Line): ${badgeWidthMM}mm x ${badgeHeightMM}mm`);
    console.log(`Bleed/Fold margin: ${(designWidthMM - badgeWidthMM) / 2}mm each side`);

    // Calculate layout
    const availableWidthMM = A4_WIDTH_MM - (2 * MARGIN_MM);
    const availableHeightMM = A4_HEIGHT_MM - (2 * MARGIN_MM);

    const cols = Math.floor((availableWidthMM + GUTTER_MM) / (designWidthMM + GUTTER_MM));
    const rows = Math.floor((availableHeightMM + GUTTER_MM) / (designHeightMM + GUTTER_MM));

    // Center the grid
    const totalGridWidthMM = cols * designWidthMM + (cols - 1) * GUTTER_MM;
    const totalGridHeightMM = rows * designHeightMM + (rows - 1) * GUTTER_MM;
    const startXMM = (A4_WIDTH_MM - totalGridWidthMM) / 2;
    const startYMM = (A4_HEIGHT_MM - totalGridHeightMM) / 2;

    // Draw each badge
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const badgeIndex = row * cols + col;

        const xMM = startXMM + col * (designWidthMM + GUTTER_MM);
        const yMM = startYMM + row * (designHeightMM + GUTTER_MM);
        const centerXMM = xMM + designWidthMM / 2;
        const centerYMM = yMM + designHeightMM / 2;

        const badgeData = badges[badgeIndex];
        const hasImage = badgeData?.layers?.some(l => l.type === 'image');

        // Draw badge with image
        if (hasImage) {
          const badgeImage = await renderBadgeImage(
            badgeData, badge, designWidthMM, designHeightMM, badgeWidthMM, badgeHeightMM
          );
          // Image is drawn at the full DESIGN size
          pdf.addImage(badgeImage, 'PNG', xMM, yMM, designWidthMM, designHeightMM);
        } else {
          // Empty badge - white background
          pdf.setFillColor(255, 255, 255);
          if (badge.type === 'round') {
            pdf.circle(centerXMM, centerYMM, designWidthMM / 2, 'F');
          } else {
            pdf.rect(xMM, yMM, designWidthMM, designHeightMM, 'F');
          }
        }

        if (showGuides) {
          // 1. INNER CUT LINE (Solid Black)
          // Indicates the actual badge size (e.g., 58mm)
          pdf.setDrawColor(0, 0, 0); // Black
          pdf.setLineWidth(0.3);     // Standard line width
          pdf.setLineDashPattern([], 0); // Solid line

          if (badge.type === 'round') {
            pdf.circle(centerXMM, centerYMM, badgeWidthMM / 2, 'S');
          } else {
            const bleedXMM = (designWidthMM - badgeWidthMM) / 2;
            const bleedYMM = (designHeightMM - badgeHeightMM) / 2;
            pdf.rect(xMM + bleedXMM, yMM + bleedYMM, badgeWidthMM, badgeHeightMM, 'S');
          }

          // 2. OUTER BLEED LINE (Black Dashed)
          // Indicates the full print/design area (e.g., 70mm)
          pdf.setDrawColor(0, 0, 0);   // Black for maximum visibility
          pdf.setLineWidth(0.1);       // Thin line
          pdf.setLineDashPattern([2, 2], 0);   // Standard dash

          if (badge.type === 'round') {
            pdf.circle(centerXMM, centerYMM, designWidthMM / 2, 'S');
          } else {
            pdf.rect(xMM, yMM, designWidthMM, designHeightMM, 'S');
          }
        }
      }
    }

    // Add 10mm reference line for scale verification
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(10, A4_HEIGHT_MM - 15, 20, A4_HEIGHT_MM - 15);
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text('10mm Scale Check', 15, A4_HEIGHT_MM - 17, { align: 'center' });

    // Add CRITICAL printing instruction
    pdf.setFontSize(12);
    pdf.setTextColor(220, 0, 0); // RED
    pdf.text('IMPORTANT: PRINT AT 100% SCALE / ACTUAL SIZE (DO NOT FIT TO PAGE)', 105, A4_HEIGHT_MM - 15, { align: 'center' });

    // Save
    const filename = `badges_${badge.id}_${badgeWidthMM}mm_${Date.now()}.pdf`;
    pdf.save(filename);

    console.log(`PDF saved: ${filename}`);
    return filename;

  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
}
